import React, { useState, useRef, useEffect, useCallback } from "react";
import { format, parseISO } from "date-fns";

import { useAppointmentMode, useAppointmentData, useSlots, useAppointmentSubmit } from "./hooks";
import { buildShipmentFieldConfig, buildCarrierFieldConfig, buildInfoData } from "./fieldConfigs";
import { formatDateOnly, deriveTypeInfo, buildInitialShipments } from "./utils";
import {
  ReadOnlyBanner, CancelledBanner, CarrierDropTrailerBanner, AppointmentTypeBadge,
  DockSlotsSection, ShipmentSection, CarrierSection, AllSlotsModal,
} from "./components";
import notify from "../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import StickyHeader from "../../../../../components/ModuleCommon/StickyHeader/StickyHeader";

export const APPOINTMENT_MODES = { ADD: "add", UPDATE: "update", READONLY: "readonly" };

/**
 * AppointmentForm  (orchestrator)
 *
 * Wires hooks together, derives display data, renders composed sub-components.
 *
 * Props:
 *  - appointmentData  object  — appointment record to display / edit
 *  - userType         string  — "Shipper" | "Carrier"
 *  - mode             string  — APPOINTMENT_MODES value (default: "update")
 */
const AppointmentForm = ({ appointmentData, userType, mode = APPOINTMENT_MODES.UPDATE }) => {
  const isShipper = userType === "Shipper";
  const headerRef = useRef(null);

  // ── 1. Mode (re-evaluated once shipmentData loads) ───────────────────────────
  const [shipmentDataForMode, setShipmentDataForMode] = useState(null);
  // Carriers can only view drop trailer appointments, so derive the type early
  // (seeded from appointmentData while shipmentData loads) to lock the form.
  const { isDropTrailer } = deriveTypeInfo(shipmentDataForMode, appointmentData);
  const { isAddMode, isReadOnlyMode, isEditMode, isCarrierDropTrailer } = useAppointmentMode(
    mode,
    shipmentDataForMode,
    { userType, isDropTrailer }
  );
  const warehouseFieldType = isShipper ? (isEditMode ? "dropdown" : "text") : "text";

  // ── 2. Remote data ───────────────────────────────────────────────────────────
  const data = useAppointmentData({ appointmentData, userType, isEditMode, isShipper });
  useEffect(() => { setShipmentDataForMode(data.shipmentData); }, [data.shipmentData]);

  // ── 3. Local form state ──────────────────────────────────────────────────────
  const [date, setDate] = useState(appointmentData?.startTime?.split("T")[0] || "");
  useEffect(() => {
    if (appointmentData?.startTime) setDate(appointmentData.startTime.split("T")[0]);
  }, [appointmentData?.startTime]);

  const [shipments, setShipments] = useState(() => buildInitialShipments(appointmentData));
  const [selectedShipmentNumber, setSelectedShipmentNumber] = useState(
    appointmentData?.shipmentNumber || ""
  );

  const isCancelled =
    appointmentData?.cancelled ||
    appointmentData?.status === "CANCELLED" ||
    appointmentData?.fetchError === "not_found";

  // ── 4. Slots ─────────────────────────────────────────────────────────────────
  const slots = useSlots({
    date,
    selectedWarehouse: data.selectedWarehouse,
    selectedDocks:     data.selectedDocks,
    dockOptions:       data.dockOptions,
    isReadOnlyMode,
    isEditMode,
    isCancelled,
    appointmentData,
  });

  // Auto-fetch on dependency change (debounced)
  const shouldFetchRef = useRef(true);
  useEffect(() => {
    if (isCancelled || !date || !data.selectedWarehouse || !data.selectedDocks) return;
    shouldFetchRef.current = true;
    const t = setTimeout(() => {
      if (shouldFetchRef.current) { slots.handleFindSlots(); shouldFetchRef.current = false; }
    }, 400);
    return () => { clearTimeout(t); shouldFetchRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, data.selectedWarehouse?.locationRef, data.selectedDocks?.dockId]);

  useEffect(() => {
    if (!slots.showAllSlotsModal && !slots.showInlineAllSlots) return;
    if (!date || !data.selectedWarehouse?.locationRef) return;
    slots.fetchAllDockSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots.showAllSlotsModal, slots.showInlineAllSlots, date, data.selectedWarehouse?.locationRef]);

  // ── 5. Form reset ────────────────────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setDate("");
    slots.setSelectedSlot(null);
    data.setSelectedLocation(null);  data.setLocationQuery("");
    data.setSelectedWarehouse(null); data.setWarehouseQuery("");
    data.setSelectedDocks(null);     data.setDockQuery("");
    data.setSelectedDoor(null);      data.setDoorQuery("");
    setShipments(buildInitialShipments(null));
    data.setAppointmentCarrier({ driverName: "", carrierName: "", trailerNumber: "" });
  }, [slots, data]);

  // ── 6. Submit ────────────────────────────────────────────────────────────────
  const { loading, handleSubmit } = useAppointmentSubmit({
    isAddMode, isReadOnlyMode, isCancelled, appointmentData,
    date,
    selectedLocation:  data.selectedLocation,
    selectedWarehouse: data.selectedWarehouse,
    selectedDocks:     data.selectedDocks,
    selectedDoor:      data.selectedDoor,
    selectedSlot:      slots.selectedSlot,
    shipments,
    selectedShipmentNumber,
    appointmentCarrier: data.appointmentCarrier,
    shipmentData:       data.shipmentData,
    userType,
    refreshAndValidateSelectedSlot: slots.refreshAndValidateSelectedSlot,
    resetForm,
  });

  // ── 7. Header info ───────────────────────────────────────────────────────────
  const calculatedMinDate = (() => {
    const stop = data.shipmentData?.shipmentStops?.find(
      (s) => s.stopName === data.selectedLocation?.locationName
    );
    const mustPickUpBy = formatDateOnly(stop?.scheduledGateIn) ?? null;
    const today = format(new Date(), "yyyy-MM-dd");
    return mustPickUpBy ? (mustPickUpBy > today ? mustPickUpBy : today) : today;
  })();

  const infoData = buildInfoData({
    selectedLocation:  data.selectedLocation,
    locationOptions:   data.locationOptions,
    selectedWarehouse: data.selectedWarehouse,
    warehouseOptions:  data.warehouseOptions,
    date, isReadOnlyMode, isEditMode, isShipper, warehouseFieldType,
    calculatedMinDate,
  });

  const handleInfoChange = (field, newValue) => {
    if (isReadOnlyMode) return;
    if (field === "Appointment Date") {
      const mustPickUpBy = formatDateOnly(data.shipmentData?.pickupDate);
      if (mustPickUpBy && mustPickUpBy !== "—" && newValue < mustPickUpBy) {
        notify.warning({
          title:   "Invalid Date",
          message: `The appointment date cannot be before the Planned Pickup Date (${format(parseISO(mustPickUpBy), "MMM dd, yyyy")}).`,
        });
        return;
      }
      setDate(newValue);
    }
    if (field === "Warehouse") {
      const selected = data.warehouseOptions.find((wh) => wh.locationName === newValue);
      if (selected) {
        data.setSelectedWarehouse(selected);
        data.setWarehouseQuery(selected.locationName);
        data.setSelectedDocks(null);
        data.setDockQuery("");
        slots.setSelectedSlot(null);
      }
    }
  };

  // ── 8. Slot selection bridges (hook needs setters from data hook) ─────────────
  const handleSelectInlineSlot = (dock, slot, slotsForDock) =>
    slots.handleSelectInlineSlot(dock, slot, slotsForDock, {
      setSelectedDocks: data.setSelectedDocks,
      setDockQuery:     data.setDockQuery,
    });

  const handleSelectSlotFromModal = (dock, slot) =>
    slots.handleSelectSlotFromModal(dock, slot, {
      setSelectedDocks: data.setSelectedDocks,
      setDockQuery:     data.setDockQuery,
    });

  // ── 9. Field configs ─────────────────────────────────────────────────────────
  const mustPickUpBy  = formatDateOnly(data.shipmentData?.pickupDate)  ?? "—";
  const mustDeliverBy = formatDateOnly(data.shipmentData?.deliveryDate) ?? "—";
  const shipmentFieldConfig = buildShipmentFieldConfig(appointmentData, mustPickUpBy, mustDeliverBy, data.shipmentData);
  const carrierFieldConfig  = buildCarrierFieldConfig(data.appointmentCarrier);
  const typeInfo            = deriveTypeInfo(data.shipmentData, appointmentData);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="w-full h-full flex flex-col">
        <div ref={headerRef} data-app-sticky-header className="sticky top-0 z-10 bg-white shadow-md w-full">
          <StickyHeader
            leftLabel="Appointment Details"
            infoData={infoData}
            mode={null}
            onInfoChange={handleInfoChange}
            templates={[]}
            onSelectTemplate={() => {}}
            buttons={
              isEditMode
                ? [{
                    label:    isAddMode ? (loading ? "Adding…" : "Add Appointment") : (loading ? "Updating…" : "Update Appointment"),
                    onClick:  handleSubmit,
                    disabled: loading || isCancelled,
                    type:     "primary",
                  }]
                : []
            }
            saveTemplateConfig={null}
            showStatusBlink={false}
          />
        </div>

        <div className="bg-white shadow-fluid-card p-fluid-3 space-y-fluid-3">
          {isReadOnlyMode && !isCancelled &&
            (isCarrierDropTrailer ? <CarrierDropTrailerBanner /> : <ReadOnlyBanner />)}
          {isCancelled && <CancelledBanner />}

          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Appointment Summary</h2>
            <AppointmentTypeBadge
              isDropTrailer={typeInfo.isDropTrailer}
              label={typeInfo.label}
              pending={!typeInfo.resolved}
            />
          </div>

          <DockSlotsSection
            appointmentData={appointmentData}
            isCancelled={isCancelled}
            isReadOnlyMode={isReadOnlyMode}
            allDockSlots={slots.allDockSlots}
            isAllSlotsLoading={slots.isAllSlotsLoading}
            allSlotsError={slots.allSlotsError}
            selectedSlot={slots.selectedSlot}
            selectedDocks={data.selectedDocks}
            onSelectSlot={handleSelectInlineSlot}
            date={date}
            selectedWarehouse={data.selectedWarehouse}
            dockOptions={data.dockOptions}
          />

          <ShipmentSection
            appointmentData={appointmentData}
            shipmentData={data.shipmentData}
            fieldConfig={shipmentFieldConfig}
            isReadOnlyMode={isReadOnlyMode}
            isEditMode={isEditMode}
          />

          <CarrierSection
            appointmentData={appointmentData}
            fieldConfig={carrierFieldConfig}
            isReadOnlyMode={isReadOnlyMode}
            isEditMode={isEditMode}
            onCarrierChange={(key, val) =>
              data.setAppointmentCarrier((prev) => ({ ...prev, [key]: val }))
            }
          />
        </div>
      </div>

      <AllSlotsModal
        isOpen={slots.showAllSlotsModal}
        onClose={() => slots.setShowAllSlotsModal(false)}
        date={date}
        allDockSlots={slots.allDockSlots}
        isLoading={slots.isAllSlotsLoading}
        error={slots.allSlotsError}
        isReadOnlyMode={isReadOnlyMode}
        onSelectSlot={handleSelectSlotFromModal}
      />
    </>
  );
};

export default AppointmentForm;
